import { useCallback, useEffect, useRef } from "react";

import { debounce } from "es-toolkit";

const IDLE_MS = 100;
const MAX_WAIT_MS = 1000;

const supportsScrollEnd = typeof window !== "undefined" && "onscrollend" in window;

type ScrollTarget = Window | Document | Element;

export function useWaitAfterScroll(target: ScrollTarget | null | undefined) {
  const waitersRef = useRef(new Set<() => void>());
  const scrollingRef = useRef(false);

  useEffect(() => {
    const eventTarget: EventTarget | null = target ?? null;

    if (!eventTarget) return undefined;

    const waiters = waitersRef.current;

    const settle = debounce(
      () => {
        scrollingRef.current = false;

        const pending = [...waiters];
        waiters.clear();
        pending.forEach((resolve) => resolve());
      },
      supportsScrollEnd ? MAX_WAIT_MS : IDLE_MS,
    );

    const handleScroll = () => {
      scrollingRef.current = true;
      settle();
    };

    eventTarget.addEventListener("scroll", handleScroll, { passive: true });
    eventTarget.addEventListener("scrollend", settle.flush);

    return () => {
      scrollingRef.current = false;
      eventTarget.removeEventListener("scroll", handleScroll);
      eventTarget.removeEventListener("scrollend", settle.flush);
      settle.cancel();
      waiters.clear();
    };
  }, [target]);

  return useCallback(() => {
    if (!scrollingRef.current) {
      return undefined;
    }

    return new Promise<void>((resolve) => {
      if (scrollingRef.current) {
        waitersRef.current.add(resolve);
      } else {
        resolve();
      }
    });
  }, []);
}
